import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import api from '../utils/api';

const Gallery = () => {
  const [gallery, setGallery] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchGallery();
  }, [selectedCategory]);

  const fetchGallery = async () => {
    try {
      const category = selectedCategory === 'all' ? null : selectedCategory;
      const response = await api.getGallery(category);
      setGallery(response.data);
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
    }
  };

  const categories = [
    { value: 'all', label: 'All Events' },
    { value: 'corporate', label: 'Corporate' },
    { value: 'wedding', label: 'Weddings' },
    { value: 'government', label: 'Government' },
    { value: 'private', label: 'Private Events' },
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="gallery-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-4 block">
              Our Portfolio
            </span>
            <h1 className="text-5xl md:text-7xl font-medium leading-[0.9] mb-6 tracking-tight">
              Moments That
              <br />
              <span className="text-gradient">Inspire</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-white/70 max-w-3xl">
              Explore our collection of carefully curated events. Each project tells a unique story of creativity,
              precision, and unforgettable experiences.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-6 py-3 text-xs uppercase tracking-wider font-semibold transition-all ${
                  selectedCategory === category.value
                    ? 'bg-[#D4AF37] text-black'
                    : 'bg-transparent border border-white/20 text-white hover:border-[#D4AF37]'
                }`}
                data-testid={`filter-${category.value}`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {gallery.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/60 text-lg">No events in this category yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {gallery.map((item, index) => (
                <motion.div
                  key={item.gallery_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group cursor-pointer"
                  data-testid={`gallery-item-${index}`}
                >
                  <div className="relative overflow-hidden aspect-[4/3] mb-4">
                    <img
                      src={item.images[0]}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <span className="text-xs uppercase tracking-wider text-[#D4AF37] font-semibold">
                          {item.category}
                        </span>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-xl font-medium mb-2 group-hover:text-[#D4AF37] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Gallery;